// Typed access to the procedural sprite pack imported from Claude Design
// ("Farm Sprite Pack"). The pack ships as four files, loaded in dependency
// order: the core generator, then the P0 extras, the creatures and the world
// set, each of which builds on the core's primitives. All four are kept
// verbatim so they can be re-synced from the design project; everything below
// is just types.
import "./sprites.js";
import "./sprites-extras.js";
import "./sprites-creatures.js";
import "./sprites-world.js";

export interface Grid {
  w: number;
  h: number;
  d: string[];
}

export interface SpriteEntry {
  cat: string;
  id: string;
  grid: Grid;
  w: number;
  h: number;
  autotile?: string;
  crop?: string;
  stage?: string;
  stageIndex?: number;
  anim?: string;
  frame?: number;
  season?: string;
  npc?: string;
  tier?: string;
  overlay?: boolean;
  /** FX sprites carry partial alpha and belong on the blended layer */
  blended?: boolean;
  priority?: string;
}

export interface CropCfg {
  id: string;
  name: string;
  kind: string;
  a: string;
  b: string;
}

export type FarmerDir = "down" | "left" | "right" | "up";

export interface FarmerOptions {
  dir?: FarmerDir;
  pose?: string;
  legs?: number;
  bob?: number;
  tool?: string | null;
  sweep?: boolean;
}

export interface SpriteLib {
  PAL: Record<string, string | null>;
  /** partial-alpha colours the FX layer may use; the companion files fill it in */
  PAL_FX: Record<string, string>;
  /** metal recolour maps for the tool tiers */
  TIERS: Record<string, Record<string, string>>;
  all(): SpriteEntry[];
  toCanvas(grid: Grid, scale?: number, mk?: (w: number, h: number) => HTMLCanvasElement): HTMLCanvasElement;
  farmer(o: FarmerOptions): Grid;
  farmerDown(kind: string): Grid;
  cropSprite(cfg: CropCfg, stageIdx: number): Grid;
  tool(id: string): Grid;
  CROPS: CropCfg[];
  STAGES: string[];
  G(w: number, h: number): Grid;
  px(g: Grid, x: number, y: number, c: string): void;
  rect(g: Grid, x: number, y: number, w: number, h: number, c: string): void;
  disc(g: Grid, cx: number, cy: number, rx: number, ry: number, c: string): void;
  outline(g: Grid, c: string): Grid;
  flipX(g: Grid): Grid;
  stamp(dst: Grid, src: Grid, ox: number, oy: number): Grid;
  recolor(g: Grid, map: Record<string, string>): Grid;
  hash(x: number, y: number, s?: number): number;
  /** the pack's own terrain generators, exposed so companions can build on them */
  __grass(seed: number): Grid;
  __tilled(wet: boolean): Grid;
  __transition(baseFn: () => Grid, overFn: () => Grid, dir: string): Grid;
  __fence(kind: string): Grid;
  __water(): Grid;
}

export interface SpriteExtrasLib {
  extras(): SpriteEntry[];
}
export interface SpriteCreaturesLib {
  creatures(): SpriteEntry[];
}
export interface SpriteWorldLib {
  world(): SpriteEntry[];
}

type Globals = {
  SpriteLib: SpriteLib;
  SpriteExtras: SpriteExtrasLib;
  SpriteCreatures: SpriteCreaturesLib;
  SpriteWorld: SpriteWorldLib;
};

const g = globalThis as unknown as Globals;

export const Lib = g.SpriteLib;
export const Extras = g.SpriteExtras;
export const Creatures = g.SpriteCreatures;
export const World = g.SpriteWorld;

/** Every sprite the vendor pack and its three companion files generate. */
export function packSprites(): SpriteEntry[] {
  return [...Lib.all(), ...Extras.extras(), ...Creatures.creatures(), ...World.world()];
}

/** Tile edge, in pixels. Characters are 24x32. */
export const TILE_PX = 24;
